

import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { hexToRgba } from "./HexToRGBA";
import axiosInstance from "./Authorization/client";
import { Slider } from "./Slider";
import { SLIDER_TYPES } from "../utils/Enum";
import { ModalLoadingScreen } from "./LoadingScreen";
const axios = axiosInstance
// --- THEME DEFINITION ---
const THEME = {
  mainBg: "#080c16",
  secondaryBg: "#131a30",
  mainColor: "#FF6978",
  secondaryColor: "#b266ff",
  accentColor: "#868be3",
  background: "#0d1324",
  successColor: "#05c483",
  warningColor: "#ffb74d",
  errorColor: "#ff4d4d",
};

// --- ANIMATION KEYFRAMES ---
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(255, 105, 120, 0.2), 0 0 30px rgba(255, 105, 120, 0.1); }
  50% { box-shadow: 0 0 25px rgba(255, 105, 120, 0.4), 0 0 45px rgba(255, 105, 120, 0.2); }
`;

// --- STYLED COMPONENTS ---
const StoreWrapper = styled.div`
  min-height: 100vh;
  color: : ${props => props.theme.secondaryBg};
  background-color: ${props => props.theme.mainBg};
  padding: 40px 20px;
  box-sizing: border-box;
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
  position: relative;

  @media (min-width: 768px) {
    padding: 64px 48px;
  }
`;

const StoreContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ToastNotification = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 100;
  max-width: 384px;
  width: calc(100% - 48px);
  background-color: #131a30;
  border: 1px solid ${props => 
    props.type === "error" ? props.theme.errorColor : 
    props.type === "warning" ? props.theme.warningColor : props.theme.successColor
  };
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToastTitle = styled.h4`
  font-weight: 700;
  font-size: 0.875rem;
  margin: 0;
  color: ${props => 
    props.type === "error" ? props.theme.errorColor : 
    props.type === "warning" ? props.theme.warningColor : props.theme.successColor
  };
`;

const ToastText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  margin-top: 6px;
  margin-bottom: 0;
  line-height: 1.625;
`;

const ToastClose = styled.button`
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  &:hover {
    color: ${props => props.theme.secondaryBg};
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const TitleBlock = styled.div``;

const Subtitle = styled.span`
  color: ${props => props.theme.mainColor};
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  display: block;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 900;
  margin: 0;
  letter-spacing: -0.025em;
  background: linear-gradient(to right, ${props => props.theme.secondaryBg}, ${props => props.theme.accentColor});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const DescText = styled.p`
  color: ${props => props.theme.accentColor};
  font-size: 0.875rem;
  margin-top: 8px;
  margin-bottom: 0;
  max-width: 500px;
  line-height: 1.625;
`;

const UserStatusBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  background-color: ${props => props.theme.secondaryBg};
  border: 1px solid ${props => props.theme.mainBg};
  padding: 16px;
  border-radius: 16px;
  align-items: center;
  width: 100%;

  @media (min-width: 768px) {
    padding: 16px 24px;
    width: auto;
  }
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    margin-right: 12px;
  }
`;

const StatusLabel = styled.span`
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
`;

const StatusValue = styled.span`
  font-size: 0.875rem;
  font-weight: 800;
  color: ${props => props.highlight ? THEME.mainColor : "${props => props.theme.secondaryBg}"};
`;

const StatusButton = styled.button`
  background: ${props => props.connected ? "rgba(255, 77, 77, 0.1)" : THEME.mainColor};
  color: ${props => props.connected ? THEME.errorColor : "#080c16"};
  border: ${props => props.connected ? `1px solid rgba(255, 77, 77, 0.2)` : "none"};
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TabSection = styled.div`
  display: flex;
  gap: 12px;
  border-bottom: 1px solid ${props => props.theme.secondaryColor};
  margin-bottom: 32px;
`;

const TabButton = styled.button`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? THEME.mainColor : "transparent"};
  color: ${props =>  props.theme.secondaryBg };
  font-size: 1rem;
  font-weight: 700;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: props => props.theme.secondaryBg;
  }
`;

const StoreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const VoucherTicket = styled.div`
  background: linear-gradient(135deg, rgba(178, 102, 255, 0.05), #0d1324);
  border: 1px solid rgba(255, 105, 120, 0.15);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-8px);
    border-color: ${props => props.color};
    box-shadow: 0 0 25px ${props => props.color}4d;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: #080c16;
    border-radius: 50%;
    top: 60%;
    z-index: 5;
  }
  &::before { left: -10px; }
  &::after { right: -10px; }
`;

const CardArtBlock = styled.div`
  width: 100%;
  height: 140px;
  border-radius: 12px;
  background: ${props => props.bgGradient};
  margin-bottom: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FloatingIcon = styled.div`
  font-size: 3rem;
  filter: drop-shadow(0 10px 8px rgba(0, 0, 0, 0.4));
  animation: ${pulse} 3s infinite ease-in-out;
`;

const CardValueBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background-color:${props => hexToRgba(props.theme.secondaryBg,0.83)};
  border: 1px solid ${props => hexToRgba(props.theme.secondaryColor,0.1)};
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  color: ${props => props.theme.secondaryBg};
`;

const VoucherTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin: 0 0 8px 0;
`;

const VoucherDescription = styled.p`
  font-size: 11px;
  color:${props => props.theme.secondaryColor};
  line-height: 1.625;
  margin: 0;
`;

const DividerLine = styled.div`
  border-top: 1px dashed ${props => hexToRgba(props.theme.accentColor,0.1)};
  margin: 16px 0;
`;

const PriceTag = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin-bottom: 16px;
  display: flex;
  align-items: baseline;
  gap: 4px;

  span {
    font-size: 0.75rem;
    color: ${props => props.theme.secondaryColor};
    font-weight: 600;
  }
`;

const PurchaseButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, ${props => props.btnColor || props.theme.mainColor} 0%, ${props => hexToRgba(props.theme.secondaryColor,0.6)} 100%);
  color:${props => props.theme.secondaryBg};
  border: none;
  padding: 12px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RedeemContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
`;

const LeftRedeemPanel = styled.div`
  background-color: ${props => props.theme.secondaryBg};
  border: 1px solid ${props => props.theme.secondaryColor};
  border-radius: 16px;
  padding: 24px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px;
  }

  @media (min-width: 1024px) {
    grid-column: span 7 / span 7;
  }
`;

const RightExplainPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    grid-column: span 5 / span 5;
  }
`;

const PanelHeading = styled.h3`
  font-size: 1.25rem;
  font-weight: 900;
  color:${props => props.theme.secondaryBg};
  margin: 0 0 8px 0;
`;

const FormInstruction = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  line-height: 1.625;
  margin-top: 0;
  margin-bottom: 24px;
`;

const FormLabel = styled.label`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  text-transform: uppercase;
  font-weight: 700;
  display: block;
  margin-bottom: 8px;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const Input = styled.input`
  flex: 1;
  background-color: ${THEME.background};
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryBg};
  font-family: ${props => props.monospace ? "monospace" : "inherit"};

  &::placeholder {
    color: #475569;
  }

  &:focus {
    outline: none;
    border-color: ${THEME.mainColor};
  }
`;

const FormButton = styled.button`
  background-color: ${THEME.mainColor};
  color: #080c16;
  font-weight: 800;
  border: none;
  padding: 0 24px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const VoucherCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SingleOwnedCard = styled.div`
  background-color: ${THEME.background};
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OwnedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MiniVisual = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${props => props.bgGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const OwnedTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 700;
  margin: 0;
  color: ${props => props.theme.secondaryBg};
`;

const OwnedSubtitle = styled.span`
  font-size: 10px;
  color: #64748b;
  display: block;
  margin-top: 4px;
`;

const NoticeCard = styled.div`
  background-color: ${props => hexToRgba(props.theme.mainColor,0.1)};
  border: 1px solid ${props => hexToRgba(props.theme.secondaryBg,0.3)};
  padding: 24px;
  border-radius: 16px;
  margin: 2% 0;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StepItem = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const StepNum = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  background-color: ${props => props.theme.secondaryColor};
  color: ${props => props.theme.secondaryBg};
  font-weight: 900;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepDesc = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  margin: 0;
  line-height: 1.625;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  background-color: ${THEME.secondaryBg};
  border: 1px solid #1e293b;
  border-radius: 16px;
  padding: 24px;
  max-width: 448px;
  width: 100%;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    color: ${props => props.theme.secondaryBg};
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin: 0 0 8px 0;
`;

const PayToggle = styled.div`
  display: flex;
  background-color: ${THEME.background};
  border: 1px solid #1e293b;
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const ToggleBtn = styled.button`
  flex: 1;
  border: none;
  background: ${props => props.active ? "#1e293b" : "transparent"};
  color: ${props => props.active ? "${props => props.theme.secondaryBg}" : "#64748b"};
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.secondaryBg};
  }
`;

const FormFieldsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const FormTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const CryptoBox = styled.div`
  text-align: center;
  padding: 20px;
  background-color: ${THEME.background};
  border-radius: 12px;
  border: 1px solid #1e293b;
  margin-bottom: 24px;
`;

const CryptoTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => props.theme.secondaryBg};
  display: block;
`;

const CryptoDesc = styled.p`
  font-size: 11px;
  color: #64748b;
  margin: 8px 0 0 0;
  line-height: 1.625;
`;

// MOCKED STORES VOUCHER DATABASE
const VOUCHER_TIERS = [
  {
    id: "silver_pass",
    title: "Swerl Silver Pass",
    description: "Unlocks 1 month of unlimited music streaming on Swerl. Automatically funds artists on-chain as you listen.",
    price: 5.0,
    allocation: 5.0,
    bgGradient: "linear-gradient(135deg, #a1a8ba 0%, #303740 100%)",
    color: "#a1a8ba",
    icon: "🎫"
  },
  {
    id: "gold_pass",
    title: "Swerl Gold Pass",
    description: "The classic mid-tier streaming pass. Unlocks 3 full months of high-definition streaming with standard value-saving bonus.",
    price: 14.0,
    allocation: 15.0,
    bgGradient: "linear-gradient(135deg, #FFD700 0%, #b25900 100%)",
    color: "#FFD700",
    icon: "🥇"
  },
  {
    id: "platinum_pass",
    title: "Swerl Platinum Pass",
    description: "A premium 6-month digital access card. Perfect for continuous active supporting and tracking collector milestones.",
    price: 28.0,
    allocation: 30.0,
    bgGradient: "linear-gradient(135deg, #b2e2ff 0%, #1a5c8c 100%)",
    color: "#b2e2ff",
    icon: "💎"
  },
  {
    id: "apex_crown",
    title: "Swerl Apex Crown",
    description: "One year of unlimited high-fidelity music streaming. Features native access pass to future VIP digital events on Sonar Muse.",
    price: 56.0,
    allocation: 60.0,
    bgGradient: "linear-gradient(135deg, #FF6978 0%, #4b0082 100%)",
    color: "#FF6978",
    icon: "👑"
  }
];



// --- MAIN PORTAL COMPONENT ---
export default function App() {
  const [activeTab, setActiveTab] = useState("store"); 
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [allocatedBalance, setAllocatedBalance] = useState(5.0); 
  
  const [activeModal, setActiveModal] = useState(null); 
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [payMethod, setPayMethod] = useState("card"); 
  const [customVoucherCode, setCustomVoucherCode] = useState("");
  const [notification, setNotification] = useState(null);

  const [cardNo, setCardNo] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCVV, setCardCVV] = useState("");

  const [ownedVouchers, setOwnedVouchers] = useState([
    {
      id: "own_1",
      title: "Silver Voucher Pass #892",
      allocation: 5.0,
      bgGradient: "linear-gradient(135deg, #a1a8ba 0%, #303740 100%)",
      redeemable: true
    }
  ]);
  const [swerlVouchers, setSwerlVouchers] = useState([]);
  const [isSwerlLoaded, setSwerlLoaded] = useState(false);

    const getSwerlVouchers = async () => {
    return await axios
      .get(`/api/getSwerlVouchers`)
      .then((vouchers) => {
        if (vouchers.data && !vouchers?.data?.error) {
          return vouchers.data;
        }
        return false;
      })
      .catch((err) => {
        console.error(err);
        return false;
      });
  };

  useEffect(() => {
    getSwerlVouchers()
      .then((vouchers) => {
        if (vouchers) {
          setSwerlVouchers(vouchers);
        }
        setSwerlLoaded(true);
      })
      .catch((err) => {
        setSwerlLoaded(true);
      });
    return () => {};
  }, []);

  const triggerToast = (title, message, type = "success") => {
    setNotification({ title, message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const toggleWallet = () => {
    if (walletConnected) {
      setWalletConnected(false);
      setWalletAddress(null);
      triggerToast("Wallet Disconnected", "Web3 interaction has been closed.", "warning");
    } else {
      setWalletConnected(true);
      setWalletAddress("rSwErL1n6PaYt1CkpXvRszgH71B2dJw8u");
      triggerToast("Wallet Authenticated", "Connected via Gem Wallet. Direct balance queries active.", "success");
    }
  };

  const initPurchase = (voucher) => {
    setSelectedVoucher(voucher);
    setActiveModal("purchase");
  };

  const handlePurchase = () => {
    if (payMethod === "card") {
      if (!cardNo) {
        triggerToast("Payment Failed", "Please fill in standard credit card validation fields.", "error");
        return;
      }
      triggerToast("Payment Captured", `Processed successfully! Your NFT Voucher "${selectedVoucher.title}" is being minted...`, "success");
    } else {
      if (!walletConnected) {
        triggerToast("Signature Required", "Please connect your XRPL wallet first to authorize the transaction payload.", "warning");
        return;
      }
      triggerToast("Ledger Verified", "XRP payment transaction was signed and validated: tesSUCCESS.", "success");
    }

    const newVoucher = {
      id: "own_" + Date.now(),
      title: `${selectedVoucher.title} #${Math.floor(Math.random() * 900) + 100}`,
      allocation: selectedVoucher.allocation,
      bgGradient: selectedVoucher.bgGradient,
      redeemable: true
    };

    setOwnedVouchers([...ownedVouchers, newVoucher]);
    setActiveModal(null);
    setSelectedVoucher(null);
    setCardNo("");
    setCardExpiry("");
    setCardCVV("");
  };

  const handleRedeemCode = () => {
    if (!customVoucherCode) return;

    if (customVoucherCode.trim().toLowerCase() === "swerlbags") {
      setAllocatedBalance(prev => prev + 5.0);
      triggerToast("Voucher Redeemed!", "Successfully credited $5.00 from XRPBags partnership to your account!", "success");
      setCustomVoucherCode("");
    } else {
      triggerToast("Verification Failed", "This voucher key was not recognized or is already inactive.", "error");
    }
  };

  const handleRedeemVoucherCard = (id, allocation, title) => {
    setAllocatedBalance(prev => prev + allocation);
    setOwnedVouchers(ownedVouchers.filter(v => v.id !== id));
    triggerToast("NFT Burn Complete", `Redeemed ${title}! Unlocked $${allocation.toFixed(2)} active streaming allocation.`, "success");
  };

  return (
    <StoreWrapper>
      <StoreContainer>
        
        {notification && (
          <ToastNotification type={notification.type}>
            <ToastHeader>
              <ToastTitle type={notification.type}>{notification.title}</ToastTitle>
              <ToastClose onClick={() => setNotification(null)}>✕</ToastClose>
            </ToastHeader>
            <ToastText>{notification.message}</ToastText>
          </ToastNotification>
        )}

        <HeaderSection>
          <TitleBlock>
            <Subtitle>Decentralized Streaming Reserves</Subtitle>
            <Title>Swərl NFT Vouchers</Title>
            <DescText>
              Acquire cryptographic vouchers to easily top up your platform streaming reserves or buy digital access tokens to support ecosystem artists.
            </DescText>
          </TitleBlock>
        </HeaderSection>
              <NoticeCard>
                <PanelHeading style={{fontSize: "1rem", marginBottom: "16px" }}>How Vouchers Work</PanelHeading>
                <StepList>
                  <StepItem>
                    <StepNum>1</StepNum>
                    <StepDesc>
                      <strong>Standard Minting:</strong> Vouchers are minted directly as NFTs on the XRP Ledger. They are completely portable and tradable.
                    </StepDesc>
                  </StepItem>
                  <StepItem>
                    <StepNum>2</StepNum>
                    <StepDesc>
                      <strong>The Burn-Redemption:</strong> Redeeming burns the NFT on-chain and transfers its dollar value directly into your Swerl Account streaming balance.
                    </StepDesc>
                  </StepItem>
                  <StepItem>
                    <StepNum>3</StepNum>
                    <StepDesc>
                      <strong>Stream Directly:</strong> Instantly use your allocation to support independent music creators on Sonar Muse and other compatible partner applications!
                    </StepDesc>
                  </StepItem>
                </StepList>
              </NoticeCard>
        <TabSection>
          <TabButton active={activeTab === "store"} onClick={() => setActiveTab("store")}>
            Voucher Storefront
          </TabButton>
        </TabSection>
                {isSwerlLoaded && swerlVouchers?.length > 0 && (
        
          <Slider
            heightScroll={true}
            type={SLIDER_TYPES.SWERL_VOUCHER}
            id={"swerl vouchers"}
            slides={swerlVouchers}
          />
       
      )}
      {!isSwerlLoaded && (
        
         <ModalLoadingScreen/>
        
      )}
         
        

           
      </StoreContainer>



    </StoreWrapper>
  );
}
